/**
 * The single reactive source of truth for the current viewer's effective preferences
 * (docs/03-architecture.md "Client state": Zustand for UI state). Everything that needs to react
 * to a preference change — the Settings page's controls, `useSfx`, `useHaptics`, `useMotionMode`
 * — reads this store instead of threading props or re-fetching `/me`.
 *
 * A subscription (below) is the *only* place that touches `<html>` attributes or localStorage —
 * `applyThemeAttributes`/`writeLocalThemePrefs` never need to be called from feature code anymore.
 */
import { create } from 'zustand';
import { DEFAULT_PREFERENCES, type UserPreferences } from '@finbob/shared';
import { applyThemeAttributes, readLocalThemePrefs, writeLocalThemePrefs } from '@/lib/theme';

type PreferenceKey = keyof UserPreferences;
interface Override {
  value: UserPreferences[PreferenceKey];
  revision: number;
  status: 'preview' | 'saving' | 'failed';
}
interface PreferenceSync {
  ownerId: string | null;
  epoch: number;
  confirmed: UserPreferences;
  overrides: Partial<Record<PreferenceKey, Override>>;
  revision: number;
  inFlight: number;
}
export interface PreferenceSave {
  ownerId: string;
  epoch: number;
  patch: Partial<UserPreferences>;
  revisions: Partial<Record<PreferenceKey, number>>;
}

interface PreferencesStore extends UserPreferences {
  sync: PreferenceSync;
  /** Explicit reset; session reads use hydrate so they preserve pending previews. */
  setAll: (prefs: UserPreferences) => void;
  /** Local preview, including slider changes that have not been submitted yet. */
  setOne: <K extends keyof UserPreferences>(key: K, value: UserPreferences[K]) => void;
  hydrate: (ownerId: string, prefs: UserPreferences) => void;
  resetSession: () => void;
  prepareSave: (patch: Partial<UserPreferences>) => PreferenceSave | undefined;
  confirmSave: (save: PreferenceSave, prefs: UserPreferences) => boolean;
  failSave: (save: PreferenceSave) => void;
  retryPatch: () => Partial<UserPreferences>;
  revertPreview: () => void;
}

function initial(): UserPreferences {
  const local = readLocalThemePrefs(); // the device-only fields: palette, accentColor, reducedGlow, glass, motion, theme
  return {
    ...DEFAULT_PREFERENCES,
    ...local,
    canvasSettings: { ...DEFAULT_PREFERENCES.canvasSettings, ...local.canvasSettings },
    canvasPattern: local.canvasPattern ?? 'linen',
    householdFont: local.householdFont ?? 'modern',
    householdNameSize: local.householdNameSize ?? 18,
  };
}

function normalize(prefs: UserPreferences): UserPreferences {
  return {
    ...DEFAULT_PREFERENCES,
    ...prefs,
    canvasSettings: { ...DEFAULT_PREFERENCES.canvasSettings, ...prefs.canvasSettings },
  };
}

function freshSync(
  prefs: UserPreferences,
  epoch = 0,
  ownerId: string | null = null,
): PreferenceSync {
  return { ownerId, epoch, confirmed: normalize(prefs), overrides: {}, revision: 0, inFlight: 0 };
}

function effective(sync: PreferenceSync): UserPreferences {
  return {
    ...sync.confirmed,
    ...Object.fromEntries(Object.entries(sync.overrides).map(([key, entry]) => [key, entry.value])),
  };
}

function sameValue(left: unknown, right: unknown): boolean {
  // Canvas settings are the only object-valued field; release/blur must not write it twice.
  return Object.is(left, right) || JSON.stringify(left) === JSON.stringify(right);
}

function withPreview(sync: PreferenceSync, patch: Partial<UserPreferences>): PreferenceSync {
  const next = { ...sync, overrides: { ...sync.overrides } };
  for (const key of Object.keys(patch) as PreferenceKey[]) {
    const value = patch[key];
    const previous = sync.overrides[key];
    if (value === undefined || sameValue(previous ? previous.value : sync.confirmed[key], value))
      continue;
    next.overrides[key] = { value, revision: ++next.revision, status: 'preview' };
  }
  return next;
}

const initialPreferences = initial();
export const usePreferencesStore = create<PreferencesStore>((set, get) => ({
  ...initialPreferences,
  sync: freshSync(initialPreferences),
  setAll: (prefs) => {
    const sync = freshSync(prefs, get().sync.epoch + 1);
    set({ ...effective(sync), sync });
  },
  setOne: (key, value) => {
    const sync = withPreview(get().sync, { [key]: value });
    set({ ...effective(sync), sync });
  },
  hydrate: (ownerId, prefs) => {
    const current = get().sync;
    if (current.ownerId !== ownerId) {
      const sync = freshSync(prefs, current.epoch + 1, ownerId);
      set({ ...effective(sync), sync });
      return;
    }
    // A refresh cannot replace either an unsaved preview or its known saved baseline.
    const confirmed = { ...normalize(prefs) };
    for (const key of Object.keys(current.overrides) as PreferenceKey[]) {
      Object.assign(confirmed, { [key]: current.confirmed[key] });
    }
    const sync = { ...current, confirmed };
    set({ ...effective(sync), sync });
  },
  resetSession: () => get().setAll(DEFAULT_PREFERENCES),
  prepareSave: (patch) => {
    const sync = withPreview(get().sync, patch);
    if (!sync.ownerId) return;
    const submitted: Partial<UserPreferences> = {};
    const revisions: PreferenceSave['revisions'] = {};
    for (const key of Object.keys(patch) as PreferenceKey[]) {
      const entry = sync.overrides[key];
      if (!entry || entry.status === 'saving') continue;
      Object.assign(submitted, { [key]: entry.value });
      revisions[key] = entry.revision;
      sync.overrides[key] = { ...entry, status: 'saving' };
    }
    if (!Object.keys(submitted).length) return;
    sync.inFlight += 1;
    set({ ...effective(sync), sync });
    return { ownerId: sync.ownerId, epoch: sync.epoch, patch: submitted, revisions };
  },
  confirmSave: (save, prefs) => {
    const current = get().sync;
    if (current.epoch !== save.epoch || current.ownerId !== save.ownerId) return false;
    const confirmed = { ...current.confirmed };
    const overrides = { ...current.overrides };
    for (const key of Object.keys(save.patch) as PreferenceKey[]) {
      Object.assign(confirmed, { [key]: prefs[key] });
      if (overrides[key]?.revision === save.revisions[key]) delete overrides[key];
    }
    const sync = { ...current, confirmed, overrides, inFlight: current.inFlight - 1 };
    set({ ...effective(sync), sync });
    return true;
  },
  failSave: (save) => {
    const current = get().sync;
    if (current.epoch !== save.epoch || current.ownerId !== save.ownerId) return;
    const overrides = { ...current.overrides };
    for (const key of Object.keys(save.patch) as PreferenceKey[]) {
      const entry = overrides[key];
      if (entry && entry.revision === save.revisions[key])
        overrides[key] = { ...entry, status: 'failed' };
    }
    const sync = { ...current, overrides, inFlight: current.inFlight - 1 };
    set({ ...effective(sync), sync });
  },
  retryPatch: () =>
    Object.fromEntries(
      Object.entries(get().sync.overrides)
        .filter(([, entry]) => entry.status !== 'saving')
        .map(([key, entry]) => [key, entry.value]),
    ) as Partial<UserPreferences>,
  revertPreview: () => {
    const current = get().sync;
    if (current.inFlight) return;
    const sync = { ...current, overrides: {} };
    set({ ...effective(sync), sync });
  },
}));

function deviceFields(state: UserPreferences) {
  const prefs = {
    palette: state.palette,
    customAccent: state.customAccent,
    canvasSettings: state.canvasSettings,
    accentColor: state.accentColor,
    canvasPattern: state.canvasPattern,
    householdFont: state.householdFont,
    householdNameSize: state.householdNameSize,
    reducedGlow: state.reducedGlow,
    glass: state.glass,
    motion: state.motion,
    theme: state.theme,
    privacyMode: state.privacyMode,
  };
  return prefs;
}

function persistDeviceFields(state: PreferencesStore): void {
  applyThemeAttributes(deviceFields(state));
  // Unsaved previews are session-local. A reload boots the last confirmed appearance.
  writeLocalThemePrefs(deviceFields(state.sync.confirmed));
}

// Keep <html data-*> and localStorage in sync with every future change. The very first paint is
// still handled synchronously in main.tsx (before this module — and React — even runs), since a
// store subscription only fires on *changes*, not on creation.
usePreferencesStore.subscribe(persistDeviceFields);
